/**
 * SubscriptionRepository - Implementation of subscription repository
 * SESSION_185: Frontend Stripe Integration
 *
 * Handles all API calls related to subscriptions.
 */

import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  GetMySubscriptionResponse,
  GetSubscriptionAccessResponse
} from '../../application/dto/subscription';

/**
 * Repository for subscription-related API operations.
 */
export class SubscriptionRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Create a new subscription for an approved business claim.
   * POST /api/subscriptions
   */
  async createSubscription(
    request: CreateSubscriptionRequest
  ): Promise<CreateSubscriptionResponse> {
    return this.apiClient.post<CreateSubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTIONS.CREATE,
      request
    );
  }

  /**
   * Cancel an existing subscription.
   * DELETE /api/subscriptions/{id}
   *
   * @param subscriptionId - The subscription ID to cancel
   * @param immediate - If true, cancels immediately. If false, cancels at end of billing period.
   */
  async cancelSubscription(
    subscriptionId: string,
    immediate: boolean = false
  ): Promise<CancelSubscriptionResponse> {
    const request: CancelSubscriptionRequest = { immediate };
    return this.apiClient.delete<CancelSubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTIONS.CANCEL(subscriptionId),
      { data: request }
    );
  }

  /**
   * Get the current user's subscription details.
   * GET /api/subscriptions/me
   */
  async getMySubscription(): Promise<GetMySubscriptionResponse> {
    return this.apiClient.get<GetMySubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTIONS.GET_MY
    );
  }

  /**
   * Get subscription access information for the current user or a specific business.
   * GET /api/subscriptions/access
   *
   * @param businessId - Optional business ID to check access for
   */
  async getSubscriptionAccess(
    businessId?: string
  ): Promise<GetSubscriptionAccessResponse> {
    const params = businessId ? { businessId } : undefined;
    return this.apiClient.get<GetSubscriptionAccessResponse>(
      API_ENDPOINTS.SUBSCRIPTIONS.GET_ACCESS,
      { params }
    );
  }
}
